import { isGlobalViewer } from '../../../constants/roles';
// src/modules/treasury/pages/AllTransfers.jsx
// HO oversight of every Inter-Branch Cash Transfer across all branches.
// Read-only monitoring with branch/status/date filters + a status summary strip.
// HO may also intervene on an in-transit transfer (confirm / reject / cancel).

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Card, Table, Typography, Spin, Alert, Row, Col, Button, Tag, Select, DatePicker,
  Tooltip, Space, Statistic, Descriptions, Popconfirm, Modal, Input, message,
} from 'antd';
import {
  SwapOutlined, ReloadOutlined, LockOutlined, ArrowRightOutlined,
  CheckOutlined, CloseOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAuth } from '../../../context/AuthContext';
import { fetchBranchById, fetchBranches } from '../../branches/api';
import {
  fetchTransfers, fetchTransferSummary, confirmTransfer, rejectTransfer, cancelTransfer,
} from '../api';
import {
  DENOMINATION_NOTES, INTER_BRANCH_TRANSFER_STATUS as ST,
  INTER_BRANCH_TRANSFER_STATUS_OPTIONS,
} from '../constants/treasuryConstants';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

const fmt = (v) => Number((v || 0).toFixed(2)).toLocaleString('en-IN');

const statusTag = (status) => {
  const o = INTER_BRANCH_TRANSFER_STATUS_OPTIONS.find((x) => x.value === status);
  return <Tag color={o?.color || 'default'}>{o?.label || status}</Tag>;
};

const DenomBreakdown = ({ denominations = {} }) => {
  const parts = DENOMINATION_NOTES.filter((n) => denominations[n.key] > 0);
  if (!parts.length) return <Text type="secondary">—</Text>;
  return (
    <Space size={[8, 4]} wrap>
      {parts.map((n) => <Tag key={n.key} style={{ margin: 0 }}>{n.label} × {denominations[n.key]}</Tag>)}
    </Space>
  );
};

export default function AllTransfers() {
  const { user } = useAuth();
  const [initLoading, setInitLoading] = useState(true);
  const [isHO, setIsHO] = useState(false);
  const [branches, setBranches] = useState([]);

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ branchId: null, status: null, dateRange: null });

  const [reasonModal, setReasonModal] = useState({ open: false, type: null, row: null });
  const [reason, setReason] = useState('');
  const [acting, setActing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        if (!user?.branch) { setInitLoading(false); return; }
        const branchRes = await fetchBranchById(user.branch);
        const branch = branchRes?.data || branchRes;
        const ho = branch?.branchType === 'HO' || isGlobalViewer(user);
        setIsHO(ho);
        if (ho) {
          const brRes = await fetchBranches({ limit: 1000 });
          setBranches(brRes?.data?.data || brRes?.data || []);
        }
      } catch {
        setError('Failed to load profile');
      } finally {
        setInitLoading(false);
      }
    })();
  }, [user]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 500 };
      if (filters.branchId) params.branchId = filters.branchId;
      if (filters.status) params.status = filters.status;
      if (filters.dateRange?.[0]) params.startDate = filters.dateRange[0].format('YYYY-MM-DD');
      if (filters.dateRange?.[1]) params.endDate = filters.dateRange[1].format('YYYY-MM-DD');
      const [listRes, sumRes] = await Promise.all([
        fetchTransfers(params),
        fetchTransferSummary(filters.branchId ? { branchId: filters.branchId } : {}),
      ]);
      setRows(listRes?.data?.data || []);
      setSummary(sumRes?.data?.data || null);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load transfers');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { if (!initLoading && isHO) load(); }, [initLoading, isHO, load]);

  const doConfirm = async (row) => {
    setActing(true);
    try {
      await confirmTransfer(row._id);
      message.success(`Confirmed receipt of ₹${fmt(row.amount)}`);
      load();
    } catch (err) {
      message.error(err?.response?.data?.message || 'Failed to confirm');
    } finally { setActing(false); }
  };

  const submitReason = async () => {
    const { type, row } = reasonModal;
    setActing(true);
    try {
      if (type === 'reject') await rejectTransfer(row._id, { reason });
      else await cancelTransfer(row._id, { reason });
      message.success('Transfer reversed to sender');
      setReasonModal({ open: false, type: null, row: null });
      load();
    } catch (err) {
      message.error(err?.response?.data?.message || 'Action failed');
    } finally { setActing(false); }
  };

  const columns = useMemo(() => [
    { title: 'Date', dataIndex: 'createdAt', width: 150, render: (v) => (v ? dayjs(v).format('DD/MM/YYYY HH:mm') : '—') },
    { title: 'Reference', dataIndex: 'reference', width: 130, render: (v) => <Text code style={{ fontSize: 12 }}>{v}</Text> },
    {
      title: 'Route', key: 'route', width: 280,
      render: (_, r) => (
        <Space size={6}>
          <Tag>{r.fromBranch?.name} ({r.fromBranch?.code})</Tag>
          <ArrowRightOutlined style={{ color: '#999' }} />
          <Tag color="blue">{r.toBranch?.name} ({r.toBranch?.code})</Tag>
        </Space>
      ),
    },
    { title: 'Amount', dataIndex: 'amount', width: 130, align: 'right', render: (v) => <Text strong>₹{fmt(v)}</Text> },
    { title: 'Status', dataIndex: 'status', width: 110, render: statusTag },
    { title: 'Initiated By', dataIndex: ['initiatedBy', 'name'], width: 140, render: (v, r) => r.initiatedBy?.name || '—' },
    {
      title: 'Action', key: 'action', width: 200, fixed: 'right',
      render: (_, row) => (row.status === ST.PENDING ? (
        <Space>
          <Popconfirm title="Confirm receipt on behalf of receiver?" okText="Confirm" onConfirm={() => doConfirm(row)}>
            <Button size="small" type="primary" icon={<CheckOutlined />}>Confirm</Button>
          </Popconfirm>
          <Button size="small" danger icon={<CloseOutlined />} onClick={() => { setReason(''); setReasonModal({ open: true, type: 'reject', row }); }}>Reject</Button>
        </Space>
      ) : <Text type="secondary">—</Text>),
    },
  ], []);

  const expandable = {
    expandedRowRender: (row) => (
      <Descriptions size="small" column={1} bordered style={{ maxWidth: 760 }}>
        <Descriptions.Item label="Denominations"><DenomBreakdown denominations={row.denominations} /></Descriptions.Item>
        {row.narration && <Descriptions.Item label="Narration">{row.narration}</Descriptions.Item>}
        {row.receivedBy && <Descriptions.Item label="Received By">{row.receivedBy?.name} · {dayjs(row.receivedAt).format('DD/MM/YYYY HH:mm')}</Descriptions.Item>}
        {row.rejectedBy && <Descriptions.Item label="Rejected">{row.rejectedBy?.name}{row.rejectionReason ? ` — ${row.rejectionReason}` : ''}</Descriptions.Item>}
        {row.cancelledBy && <Descriptions.Item label="Cancelled">{row.cancelledBy?.name}{row.cancellationReason ? ` — ${row.cancellationReason}` : ''}</Descriptions.Item>}
      </Descriptions>
    ),
  };

  if (initLoading) return <Spin style={{ display: 'block', marginTop: 80 }} />;

  if (!isHO) {
    return (
      <div style={{ padding: '80px 0', textAlign: 'center' }}>
        <LockOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
        <Title level={5} type="secondary">All Cash Transfers</Title>
        <Text type="secondary">This section is restricted to Head Office users.</Text>
      </div>
    );
  }

  return (
    <div style={{ padding: '16px 0' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={5} style={{ margin: 0 }}><SwapOutlined style={{ marginRight: 8 }} />All Inter-Branch Cash Transfers</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>Every cash movement between branches across the organisation</Text>
        </Col>
        <Col><Tooltip title="Refresh"><Button icon={<ReloadOutlined />} onClick={load} loading={loading} /></Tooltip></Col>
      </Row>

      {error && <Alert type="error" message={error} closable onClose={() => setError(null)} style={{ marginBottom: 16 }} />}

      {summary && (
        <Row gutter={12} style={{ marginBottom: 16 }}>
          <Col xs={12} sm={6}><Card size="small"><Statistic title="In Transit" value={summary.PENDING?.count || 0} suffix={`· ₹${fmt(summary.PENDING?.totalAmount)}`} valueStyle={{ color: '#fa8c16' }} /></Card></Col>
          <Col xs={12} sm={6}><Card size="small"><Statistic title="Received" value={summary.RECEIVED?.count || 0} suffix={`· ₹${fmt(summary.RECEIVED?.totalAmount)}`} valueStyle={{ color: '#389e0d' }} /></Card></Col>
          <Col xs={12} sm={6}><Card size="small"><Statistic title="Rejected" value={summary.REJECTED?.count || 0} valueStyle={{ color: '#cf1322' }} /></Card></Col>
          <Col xs={12} sm={6}><Card size="small"><Statistic title="Cancelled" value={summary.CANCELLED?.count || 0} valueStyle={{ color: '#8c8c8c' }} /></Card></Col>
        </Row>
      )}

      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[12, 8]} align="middle">
          <Col xs={24} sm={8}>
            <Select placeholder="Filter by branch" allowClear showSearch optionFilterProp="children" style={{ width: '100%' }}
              value={filters.branchId} onChange={(v) => setFilters((f) => ({ ...f, branchId: v || null }))}>
              {branches.map((b) => <Select.Option key={b._id} value={b._id}>{b.name} ({b.code})</Select.Option>)}
            </Select>
          </Col>
          <Col xs={24} sm={6}>
            <Select placeholder="Filter by status" allowClear style={{ width: '100%' }}
              value={filters.status} onChange={(v) => setFilters((f) => ({ ...f, status: v || null }))}>
              {INTER_BRANCH_TRANSFER_STATUS_OPTIONS.map((o) => <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>)}
            </Select>
          </Col>
          <Col xs={24} sm={10}>
            <RangePicker style={{ width: '100%' }} value={filters.dateRange} format="DD/MM/YYYY"
              onChange={(dates) => setFilters((f) => ({ ...f, dateRange: dates }))} />
          </Col>
        </Row>
      </Card>

      <Table
        dataSource={rows} columns={columns} rowKey="_id" loading={loading}
        size="small" scroll={{ x: 1200 }} expandable={expandable}
        pagination={{ pageSize: 25, showSizeChanger: true, showTotal: (t) => `${t} transfers` }}
        locale={{ emptyText: 'No transfers found' }}
      />

      <Modal
        title={reasonModal.type === 'reject' ? 'Reject Transfer' : 'Cancel Transfer'}
        open={reasonModal.open}
        onCancel={() => setReasonModal({ open: false, type: null, row: null })}
        onOk={submitReason}
        okText="Reverse to Sender"
        okButtonProps={{ danger: true, loading: acting }}
        destroyOnClose
      >
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message={`₹${fmt(reasonModal.row?.amount)} will be reversed back to ${reasonModal.row?.fromBranch?.name || 'the sender'}.`} />
        <Text>Reason (optional)</Text>
        <TextArea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} style={{ marginTop: 4 }} />
      </Modal>
    </div>
  );
}
